import { PostPollFactory } from "./PostPollSectionTypes/PostPollFactory";
import { PostComponentsSection } from "../Post.styles";
import { PollType, PostIdType } from "../Post.types";
import { useCallback, useState } from "react";
import {
    fetcherAddPollResponse,
    fetcherRemovePollResponse,
} from "../../../DataFetchers/PostDataFetcher";
import {
    deleteUserPoll,
    updateUserPolls,
} from "@infrastructure/Hooks/UserInteractions/useUserPolls";
import {
    RESET_API_CACHE_REASONS,
    resetIgnoreApiCacheTime,
} from "@infrastructure/Services/ApiUtility";
import { publish } from "@infrastructure/Events/EventBus";
import {
    POLL_RESPONSE_SUBMIT_ERROR,
    POLL_RESPONSE_SUBMITTED,
} from "../PostForm/events";
import { useInterruptableAction } from "@infrastructure/Persistence/UserState";

type PollActionInProgress = {
    action: "create" | "delete";
    pollOptionId: number;
    postId: PostIdType;
};

export function PostPoll({
    postId,
    data,
    content,
}: {
    data: PollType;
    postId: PostIdType;
    content?: string;
}) {
    const [pollActionInProgress, setPollActionInProgress] =
        useState<PollActionInProgress>();

    useInterruptableAction(
        pollActionInProgress,
        () =>
            pollActionInProgress &&
            performPollResponseOnPost(
                pollActionInProgress.pollOptionId,
                pollActionInProgress.action,
                pollActionInProgress.postId,
            ),
        () => setPollActionInProgress(undefined),
    );

    const performPollResponseOnPost = async (
        pollOptionId: number,
        action: string,
        postId: PostIdType,
    ) => {
        if (action !== "create" && action !== "delete") {
            return;
        }

        let pollPromise: Promise<void>;
        if (action === "create") {
            pollPromise = fetcherAddPollResponse(postId, pollOptionId).then(
                (data) => {
                    updateUserPolls(
                        data.postId,
                        data.optionId,
                        data.quizAnswerOptionId,
                    );
                },
            );
        } else {
            pollPromise = fetcherRemovePollResponse(postId).then(() =>
                deleteUserPoll(postId),
            );
        }

        return pollPromise
            .then(() => {
                resetIgnoreApiCacheTime(RESET_API_CACHE_REASONS.POST_POLL_VOTE);
                publish({
                    name: POLL_RESPONSE_SUBMITTED,
                    data: { postId },
                });
            })
            .finally(() => {
                setPollActionInProgress(undefined);
            })
            .catch((err) => {
                publish({
                    name: POLL_RESPONSE_SUBMIT_ERROR,
                    data: {
                        err,
                        postId,
                    },
                });
            });
    };

    const createPollResponseOnPost = useCallback(
        (postId: PostIdType) =>
            async (pollOptionId: number, action: "create" | "delete") => {
                setPollActionInProgress({
                    pollOptionId: pollOptionId,
                    action: action,
                    postId: postId,
                });
                return performPollResponseOnPost(pollOptionId, action, postId);
            },
        [performPollResponseOnPost, postId],
    );
    return (
        <PostComponentsSection>
            <PostPollFactory
                pollData={data}
                pollOptionHandler={createPollResponseOnPost(postId)}
                postId={postId}
                content={content}
            />
        </PostComponentsSection>
    );
}
